import { StateNames } from '#shared/statics.ts';
import {
    CartType,
    type ExhibitionInfo,
    type NeoCartItem,
    type NeoOrder,
    type NeoPaymentChannel,
    type NeoSummedPrice,
    type NeoPrice,
    type Price,
} from '@expert/types';
import { deepEqual } from '@grafana/faro-web-sdk';
import { getPrice } from '~/composables/ExpApi/expPrice';
import { mapShoppingCartFromNeo } from '~/composables/Mapper/ShoppingCart';
import type { Address } from '~~/models/Session';
import type { ItemList, ShopCartItem, ShoppingCart } from '~~/models/shoppingCart';
import { toRaw } from 'vue';
import type { ABConfigContext } from '~~/models/ABContext';
import { useNeoSession } from '~/composables/states.ts';

export const initializeCartStates = async () => {
    const logger = useLogger('ShoppingCartApi');
    const appUserProfile: Ref<AppUserProfile> = useState<AppUserProfile>(StateNames.APP_USER_PROFILE);
    const currentUserState = useCurrentUserState();

    const shoppingCartId = appUserProfile?.value?.shoppingCartId ?? currentUserState?.value?.shoppingCartId;
    const reservationCartId = appUserProfile?.value?.reservationCartId ?? currentUserState?.value?.reservationCartId;

    if (!shoppingCartId) return;
    let shoppingCart;
    try {
        const res = await fetchCurrentCart(shoppingCartId);
        shoppingCart = res?.data;
    } catch (e) {
        logger.error('failed fetching shopping cart', e);
        return;
    }

    let reservationCart;
    if (reservationCartId) {
        try {
            const res = await fetchCurrentCart(reservationCartId);
            reservationCart = res?.data;
        } catch (e) {
            logger.error('failed fetching reservation cart', e);
        }
    }

    updateCartRelevantStatesByCartType(shoppingCart?.value);
    updateCartRelevantStatesByCartType(reservationCart?.value);
};

export const fetchCurrentCart = async (shoppingCartId: string): Promise<{ data: Ref<ShoppingCart | null> }> => {
    const logger = useLogger('fetchCurrentCart');
    const cart: ShoppingCart | null = await $fetch(
        `${useRuntimeConfig().public.apiUrl}/api/neo/shopping-cart/getShoppingCart`,
        {
            query: {
                shoppingCartId: shoppingCartId,
            },
        },
    );

    return {
        data: ref(cart),
    };
};

export async function redeemVoucher(csrfToken: string, voucher: string, captcha: string) {
    let body = {};
    body.voucher = voucher;
    if (captcha) body.captcha = captcha;
    const data = await $fetch('/api/neo/frontend/_api/shoppingcart/redeemVoucher', {
        method: 'POST',
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        body: body,
    });
    return ref(data);
}

export async function addArticleToShoppingCart(
    articleId: string,
    quantity: number,
    csrfToken: string,
    options?: string[],
): Promise<ShoppingCart> {
    return addArticleToSomeCart(CartType.SHOPPING_CART, articleId, quantity, csrfToken, options);
}

export async function addArticleToReservationCart(
    articleId: string,
    quantity: number,
    csrfToken: string,
    options?: string[],
): Promise<ShoppingCart> {
    return addArticleToSomeCart(CartType.RESERVATION_CART, articleId, quantity, csrfToken, options);
}

export async function addArticleToSomeCart(
    cartType: CartType,
    articleId: string,
    quantity: number,
    csrfToken: string,
    options?: string[],
): Promise<ShoppingCart> {
    useGeneralTrick({
        eventType: 'ADD_TO_CART_ITEM',
        sessionId: useCurrentSessionIdentifier()?.value,
        json: {
            cartType: cartType,
            articleId: articleId,
            quantity: quantity,
        },
    });
    return await $fetch(`/api/neo/frontend/_api/${getPathRepresentationFromCartType(cartType)}/addItem`, {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        credentials: 'include',
        body: {
            article: articleId,
            quantity: quantity,
            options: options,
        },
        async onResponse(context) {
            const data = context.response._data;

            if (!data.shoppingCart && data.namespace) {
                throw createError({
                    statusCode: 500,
                    statusMessage: data.namespace,
                    cause: {
                        namespace: data.namespace,
                        errorcode: data.errorcode,
                    },
                    fatal: true,
                });
            }

            const cart: ShoppingCart = mapShoppingCartFromNeo(data.shoppingCart);

            if (context?.error) {
                console.error('could not add article to cart ' + context?.error);
            }

            const { data: price } = await getPrice(articleId, useCurrentStore(), false, false);
            const maxQuantity = Math.min(
                3,
                cartType === CartType.SHOPPING_CART
                    ? price?.value?.price?.onlineStock || 3
                    : price?.value?.price?.storeStock || 3,
            );
            for (const item of cart?.itemList ?? []) {
                if (item.articleId == articleId && item.quantity > maxQuantity) {
                    await modifyArticleQuantityInShoppingCart(cartType, item.id, maxQuantity, csrfToken);
                    throw createError({
                        statusCode: 500,
                        statusMessage: 'QuantityCartRule',
                        cause: {
                            namespace: 'QuantityCartRule',
                            errorcode: 9,
                        },
                        fatal: true,
                    });
                }
            }

            const validationData = await validateCart(csrfToken);
            if (validationData?.value) {
                for (const validation of Object.values(validationData.value)) {
                    for (const error of validation.failures) {
                        if (error?.namespace == 'QuantityCartRule' && error?.errorcode == 9) {
                            await modifyArticleQuantityInShoppingCart(cartType, error.id, maxQuantity, csrfToken);
                            throw createError({
                                statusCode: 500,
                                statusMessage: 'QuantityCartRule',
                                cause: error,
                                fatal: true,
                            });
                        }
                    }
                }
            }

            if (data?.shoppingCart) {
                context.response._data = cart;
                updateCartRelevantStatesByCartType(cart);
            } else {
                console.log('addArticleToSomeCart had an error');
            }
        },
    });
}

export const getValidateCartResponse = async (
    cartId: string,
    storeId: string,
    cacheLevel?: 'LIVE' | 'DEFAULT' | 'LONG',
    paymentProvider?: string,
) => {
    console.debug('getUpdatedShoppingCartWithCacheLevel', cartId, storeId, cacheLevel, paymentProvider);
    const respo = await $fetch('/api/neo/shoppingCart-service/getUpdatedShoppingCartWithCacheLevel', {
        body: {
            clientId: storeId?.startsWith('e_') ? storeId : 'e_' + storeId,
            shoppingCartId: cartId,
            priceCacheLevel: cacheLevel || 'DEFAULT',
            paymentProvider: paymentProvider,
        },
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': useCurrentUserState().value?.csrfToken,
        },
        method: 'POST',
    });
    itemListDescriptionRequestFix(respo?.newCart?.itemList);
    if (respo.newCart.lastAdded?.itemOnDisplay) {
        respo.newCart.lastAdded.itemOnDisplay.description = replaceMutetedCharacters(
            respo?.newCart?.lastAdded?.itemOnDisplay?.description,
        );
        respo.newCart.lastAdded.itemOnDisplay.type = replaceMutetedCharacters(
            respo?.newCart?.lastAdded?.itemOnDisplay?.type,
        );
    }
    itemListDescriptionRequestFix(respo?.oldCart?.itemList);
    if (respo.oldCart.lastAdded?.itemOnDisplay) {
        respo.oldCart.lastAdded.itemOnDisplay.description = replaceMutetedCharacters(
            respo?.oldCart?.lastAdded?.itemOnDisplay?.description,
        );
        respo.oldCart.lastAdded.itemOnDisplay.type = replaceMutetedCharacters(
            respo?.oldCart?.lastAdded?.itemOnDisplay?.type,
        );
    }
    console.debug('response from fetch', respo);
    if (!deepEqual(respo?.oldCart, respo?.newCart)) {
        console.warn('carts not equal');
        const failures = await $fetch('/api/neo/order-service/validateCart', {
            body: {
                clientId: storeId?.startsWith('e_') ? storeId : 'e_' + storeId,
                priceCacheLevel: cacheLevel || 'DEFAULT',
                cart: respo?.oldCart,
            },
            headers: {
                'bt-use-user-auth': 'true',
                'csrf-token': useCurrentUserState().value?.csrfToken,
            },
            method: 'POST',
        });
        return failures;
    }
};

export const updateShoppingCartWithCacheLevel = async (
    cartId: string,
    storeId: string,
    cacheLevel?: 'LIVE' | 'DEFAULT' | 'LONG',
    paymentProvider?: string,
) => {
    const respo = await $fetch('/api/neo/shoppingCart-service/getUpdatedShoppingCartWithCacheLevel', {
        body: {
            clientId: storeId?.startsWith('e_') ? storeId : 'e_' + storeId,
            shoppingCartId: cartId,
            priceCacheLevel: cacheLevel || 'DEFAULT',
            paymentProvider: paymentProvider,
        },
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': useCurrentUserState().value?.csrfToken,
        },
        method: 'POST',
    });
    itemListDescriptionRequestFix(respo?.newCart?.itemList);
    if(respo.newCart.lastAdded?.itemOnDisplay) {
        respo.newCart.lastAdded.itemOnDisplay.description = replaceMutetedCharacters(
            respo?.newCart?.lastAdded?.itemOnDisplay?.description,
        );
        respo.newCart.lastAdded.itemOnDisplay.type = replaceMutetedCharacters(
            respo?.newCart?.lastAdded?.itemOnDisplay?.type,
        );
    }
    await respo?.newCart?.itemList?.items?.forEach(async (item) => {
        console.debug('updating item', item);
        const response2 = await $fetch('/api/neo/shoppingCart-service/updateItem', {
            body: {
                shoppingCartId: cartId,
                itemId: item?.id,
                item: item,
                paymentProvider: paymentProvider,
            },
            headers: {
                'bt-use-user-auth': 'true',
                'csrf-token': useCurrentUserState().value?.csrfToken,
            },
            method: 'POST',
        });
        console.debug('got updated item response', response2);
    });
};

export async function modifyArticleQuantityInShoppingCart(
    cartType: CartType,
    articleId: string,
    quantity: number,
    csrfToken: string,
    paymentProvider?: string | null,
) {
    try {
        const data = await $fetch(
            `/api/neo/frontend/_api/${getPathRepresentationFromCartType(cartType)}/modifyItemQuantity`,
            {
                headers: {
                    'bt-use-user-auth': 'true',
                    'csrf-token': csrfToken,
                },
                method: 'POST',
                body: {
                    itemId: articleId,
                    quantity: quantity,
                    paymentProvider: paymentProvider,
                },
            },
        );
        useGeneralTrick({
            eventType: 'ADD_TO_CART_ITEM',
            sessionId: useCurrentSessionIdentifier()?.value,
            json: {
                cartType: cartType,
                articleId: articleId,
                quantity: quantity,
            },
        });

        console.log('modifyArticleQuantityInShoppingCart Response : ', data);
        return ref(data);
    } catch (error) {
        console.error('could not modify article quantity in shoppingCart ' + error);
        throw error;
    }
}

export async function addItemToShoppingCart(
    cartType: CartType,
    articleId: string,
    quantity: number,
    csrfToken: string,
) {
    const data = await $fetch(`/api/neo/frontend/_api/${getPathRepresentationFromCartType(cartType)}/addItem`, {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            article: articleId,
            quantity: quantity,
        },
    });
    return ref(data);
}

export async function validateCart(csrfToken: string) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/validateCart', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken || useCurrentUserState().value?.csrfToken,
        },
        method: 'POST',
        body: {},
    });
    return ref(data);
}

export async function validateOrder(csrfToken: string, orderId?: string) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/validateOrder', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken || useCurrentUserState().value?.csrfToken,
        },
        method: 'POST',
        body: {
            orderId: orderId,
        },
    });
    return ref(data);
}

export async function modifyAdditionalServiceOption(
    cartType: CartType,
    itemId: string,
    options: string | string[],
    state: boolean,
    csrfToken: string,
    paymentProvider?: string,
) {
    console.log('modifyAdditionalServiceOption :  ', {
        cartType: cartType,
        itemId: itemId,
        options: options,
        state: state,
        csrfToken: csrfToken,
        paymentProvider: paymentProvider,
    });
    const mySwooop =
        useFeatureState('EXSI-1263')?.value == true
        && useFeatureValue('EXSI-1263')?.value?.enabled
        && (Array.isArray(options) ? options : [options?.replace('e_', '')])?.includes(
            useFeatureValue('EXSI-1263')?.value?.expId,
        );
    if (mySwooop && state == false) {
        const { clearCart } = useMySwooop();
        clearCart();
    }
    refreshCookie(useRuntimeConfig()?.public?.mySwooop?.cartCookieName);
    try {
        const data = await $fetch(
            `/api/neo/frontend/_api/${getPathRepresentationFromCartType(cartType)}/modifyAdditionalServiceOption`,
            {
                method: 'POST',
                headers: {
                    'bt-use-user-auth': 'true',
                    'csrf-token': csrfToken,
                },
                body: {
                    state: state,
                    options: Array.isArray(options) ? options : [options?.replace('e_', '')],
                    itemId: itemId,
                    paymentProvider: paymentProvider,
                },
            },
        );
        console.log('modifyAdditionalServiceOption Response : ', data);
        return ref(data);
    } catch (error) {
        console.error('could not modify service option in cart ' + error);
        throw error;
    }
}

export async function calculateFakeShippingBins(itemList: any[], csrfToken: string) {
    itemListDescriptionRequestFix(itemList);
    try {
        const data = await $fetch('/api/checkout/calculateShippingBins', {
            headers: {
                'csrf-token': csrfToken,
            },
            method: 'POST',
            body: {
                itemList: {
                    items: itemList.map((ite) => {
                        return { ...ite, expArticle: undefined };
                    }),
                },
            },
        });
        return ref(data);
    } catch (error) {
        console.error('Error calculating fake shipping bins', error);
    }
}

export async function getShippingMethodOptionsByIds(csrfToken: string, methArray: []) {
    try {
        const data = await $fetch('/api/neo/shipping-service/getShippingMethodOptionsByIds', {
            headers: {
                'bt-use-user-auth': 'true',
                'csrf-token': csrfToken,
            },
            method: 'POST',
            body: {
                ids: methArray,
            },
        });
        return ref(data);
    } catch (error) {
        console.error('Error getting shippingmethods by ids');
    }
}

export async function reserve() {
    const valid = useReservationFormData()?.value?.valid;
    if (valid) delete useReservationFormData().value.valid;
    if (valid != JSON.stringify(useReservationFormData().value)) {
        throw 'invalid';
        return false;
    }

    let address = {
        ...useReservationFormData()?.value,
        countryCode: 'DE',
        // id: uuidv4(),
        street: useReservationFormData()?.value?.street ? useReservationFormData()?.value?.street : '',
        zip: useReservationFormData()?.value?.zip ? useReservationFormData()?.value?.zip : '',
        city: useReservationFormData()?.value?.city ? useReservationFormData()?.value?.city : '',
        houseNumber: useReservationFormData()?.value?.houseNumber ? useReservationFormData()?.value?.houseNumber : '',
    };
    let person = {
        firstName: useReservationFormData()?.value?.firstName ? useReservationFormData()?.value?.firstName : '',
        lastName: useReservationFormData()?.value?.lastName ? useReservationFormData()?.value?.lastName : '',
        title: useReservationFormData()?.value?.title ? useReservationFormData()?.value?.title : 'UNKNOWN',
        email: useReservationFormData()?.value?.email ? useReservationFormData()?.value?.email : '',
        telephone: useReservationFormData()?.value?.telephone ? useReservationFormData()?.value?.telephone : '',
        company: useReservationFormData()?.value?.company ? useReservationFormData()?.value?.company : '',
    };

    const resFeature = await $fetch('/api/neo/frontend/_api/features/get', {
        method: 'POST',
        body: {
            type: 'Reservation',
        },
        headers: {
            'csrf-token': useCurrentUserState()?.value?.csrfToken,
            'bt-use-user-auth': 'true',
        },
        server: false,
    });

    console.debug('got features', resFeature);

    delete address['email'];
    delete address['id'];
    delete address['company'];
    delete address['country'];
    delete address['fax'];
    delete address['mobile'];
    delete address['customerNumber'];
    delete address['telephone'];
    delete address['firstName'];
    delete address['lastName'];
    delete address['title'];

    let additionalData = null;

    const featureState = useFeatureState('EXSI-1263')?.value && useFeatureValue('EXSI-1263')?.value?.enabled;
    if (featureState == true) {
        const { data: cartData } = await useAsyncData('mySwooop', (_nuxt) => $fetch(`/api/_mySwooop/cart`));
        if ((cartData?.value?.items?.length ?? 0) > 0) {
            console.debug('mySwoooop', cartData);
            additionalData = { mySwooop: toRaw(cartData) };
        }
    }

    return await $fetch('/api/neo/frontend/_api/reservation/reserve', {
        method: 'POST',
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': useCurrentUserState()?.value?.csrfToken,
        },
        body: {
            person: person,
            address: address,
            useAddress: true,
            _rev: resFeature?.rev,
            text: useReservationFormAdditionalInfo()?.value?.comment,
            additionalData: additionalData,
            newsletter: useReservationFormAdditionalInfo()?.value?.newsletterOptIn,
        },
    });
}

export async function getReservation(csrfToken: string, reservationId: string) {
    const { data } = await useFetch('/api/neo/reservation-service/getReservation', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            reservationId: reservationId,
        },
    });
    return data;
}

export async function getUserOrderProcess(csrfToken: string) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/getUserOrderProcess', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {},
    });
    return { data: ref(data) };
}

export async function proceedWithRegistration(csrfToken: string) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/proceedWithRegistration', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {},
    });
    return ref(data);
}

export async function proceedAsGuest(csrfToken: string) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/proceedAsGuest', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {},
    });
    return ref(data);
}

export async function getSystemUser(csrfToken: string, systemUserId: string) {
    const { error, data } = await useFetch('/api/neo/user-service/getSystemUser', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            systemUserId: systemUserId,
        },
    });
    return data.value as NeoSystemUser;
}

export async function getBrand(csrfToken: string, brandId: string) {
    const { error, data } = await useFetch('/api/neo/article-service/getBrand', {
        key: 'brandId=' + brandId,
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            brandId: brandId,
        },
    });
    return data;
}

export async function updateOrderFlags(
    csrfToken: string,
    newsletterFlag: boolean,
    termsFlag: boolean,
    insuranceFlag: boolean,
) {
    return await $fetch('/api/neo/frontend/_api/checkout/updateOrderFlags', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            newsletterFlag: newsletterFlag,
            termsFlag: termsFlag,
            insuranceFlag: insuranceFlag,
        },
    });
}

export async function setPersonalData(csrfToken: string, requestData: Object) {
    if (requestData?.invoiceAddress && !requestData?.invoiceAddress?.telephone) {
        requestData.invoiceAddress.telephone = '';
    }
    if (requestData?.shippingAddress && !requestData?.shippingAddress?.telephone) {
        requestData.shippingAddress.telephone = '';
    }
    return await useFetch('/api/neo/frontend/_api/checkout/setPersonalData', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: requestData,
    });
}

export async function setInvoiceAddress(
    csrfToken: string,
    orderId: string,
    invoiceAddress: Object,
    isPaypal: boolean,
) {
    if (!invoiceAddress?.telephone) {
        invoiceAddress.telephone = '';
    }
    const data = await $fetch(
        '/api/neo/frontend/_api/checkout/' + (isPaypal ? 'setPaypalInvoiceAddress' : 'setInvoiceAddress'),
        {
            headers: {
                'bt-use-user-auth': 'true',
                'csrf-token': csrfToken,
            },
            method: 'POST',
            body: {
                orderId: orderId,
                invoiceAddress: invoiceAddress,
            },
        },
    );
    return ref(data);
}

export async function getOrder(csrfToken: string, orderId: string): Promise<Ref<NeoOrder | undefined>> {
    const data = await $fetch<NeoOrder>('/api/neo/order-service/getOrder', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            orderId: orderId,
        },
    });
    return ref(data);
}

export async function startOrderProcess(csrfToken: string, orderId?: string) {
    const requestData: Record<string, unknown> = {};
    const abConfigContext = useABConfigCookie().value?.context as ABConfigContext | undefined;
    const paybackCardNumber = abConfigContext?.payback?.cardNumber;
    const paybackSCRID = getScrid();
    if (orderId) requestData.orderId = orderId;
    if (paybackCardNumber) requestData.paybackCardNumber = paybackCardNumber;
    if (paybackSCRID) requestData.paybackSCRID = paybackSCRID;
    const { data } = await useFetch('/api/neo/frontend/_api/checkout/startOrderProcess', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: requestData,
    });
    return data;
}

export async function setPayBackData(csrfToken: string, orderId: string) {
    const requestData: Record<string, unknown> = {};
    const abConfigContext = useABConfigCookie().value?.context as ABConfigContext | undefined;
    const paybackCardNumber = abConfigContext?.payback?.cardNumber;
    const paybackSCRID = getScrid();

    if (orderId) requestData.orderId = orderId;
    if (paybackCardNumber) requestData.paybackCardNumber = paybackCardNumber;
    if (paybackSCRID.value) requestData.paybackSCRID = paybackSCRID.value;
    return await $fetch('/api/neo/frontend/_api/checkout/setPayBackData', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: requestData,
    }).catch(() => {
        return undefined;
    });
}

export async function changeShippingAspects(
    csrfToken: string,
    orderId: string,
    itemIds: string[],
    selectedCarrier: string,
    selectedShippingMethod: string,
    aspectOptions: string[],
) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/changeShippingAspects', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            orderId: orderId,
            itemIds: itemIds,
            selectedCarrier: selectedCarrier,
            selectedShippingMethod: selectedShippingMethod,
            aspectOptions: aspectOptions,
            noCache: new Date()
        },
    });
    return ref(data);
}

export async function getShippingCarriers(csrfToken: string) {
    const { error, data } = await useFetch('/api/neo/shipping-service/getShippingCarriers', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {},
    });
    return data;
}

//TODO: requests containing mutated characters (äöüß) currently don't work
//better fix the actual problem and not just replace characters in every request
function itemListDescriptionRequestFix(itemList) {
    let items = itemList;
    if (itemList?.items) items = itemList.items;
    items?.forEach((item) => {
        itemListDescriptionRequestFix(item?.additionalItems);
        item.itemOnDisplay.description = replaceMutetedCharacters(item.itemOnDisplay?.description);
        item.itemOnDisplay.type = replaceMutetedCharacters(item.itemOnDisplay?.type);
    });
}

function replaceMutetedCharacters(dirtyString: string) {
    let cleanedUp = dirtyString;
    try {
        cleanedUp = dirtyString
            ?.replaceAll('Ä', 'Ae')
            .replaceAll('Ö', 'Oe')
            .replaceAll('Ü', 'Ue')
            .replaceAll('ä', 'ae')
            .replaceAll('ö', 'oe')
            .replaceAll('ü', 'ue')
            .replaceAll('ß', 'ss');
    } catch (error) {
        console.warn(error);
    }
    return cleanedUp;
}

export async function getAvailablePaymentChannels(
    csrfToken: string,
    combinedShippingCost: NeoSummedPrice,
    combinedItemList: ItemList,
    userProfileId: string,
    b2b: boolean,
): Promise<Ref<NeoPaymentChannel[]>> {
    itemListDescriptionRequestFix(combinedItemList);
    const data = await $fetch<NeoPaymentChannel[]>('/api/neo/payment-service/getAvailablePaymentChannels', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            paymentChannelRequest: {
                amount: {
                    shippingCost: combinedShippingCost,
                    items: combinedItemList,
                    currency: combinedShippingCost.currency,
                },
                payer: userProfileId,
                b2b: b2b,
            },
        },
    });
    return ref(data) as Ref<NeoPaymentChannel[]>;
}

export async function getPayment(csrfToken: string, paymentId: string) {
    const { error, data } = await useFetch('/api/neo/payment-service/getPayment', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            paymentId: paymentId,
        },
    });
    return data;
}

export async function getPaymentChannel(csrfToken: string, channelId: string): Promise<Ref<NeoPaymentChannel>> {
    const data = await $fetch<NeoPaymentChannel>('/api/neo/payment-service/getPaymentChannel', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            channelId: channelId,
        },
    });
    return ref(data) as Ref<NeoPaymentChannel>;
}

export async function selectPaymentChannel(csrfToken: string, orderId: string, channelId: string) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/selectPaymentChannel', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            orderId: orderId,
            channelId: channelId,
        },
    });
    return ref(data);
}

export async function killKlarna(csrfToken: string) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/killKlarna', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {},
    });
    return ref(data);
}

export async function getKlarnaSession(csrfToken: string) {
    const data = await $fetch('/api/neo/frontend/_api/checkout/getKlarnaSession', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {},
    });
    return ref(data);
}

export async function getAllWertgarantieProductType(csrfToken: string) {
    const data = await $fetch('/api/neo/warranty-service/getAllWertgarantieProductType', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {},
    });
    return ref(data);
}

export async function finalizePendingOrder(
    csrfToken: string,
    orderId: string,
    urlSuccess: string,
    urlFailure: string,
    urlCancel: string,
    authorizationToken: string,
    captcha: string,
) {
    let body = {};
    body.orderId = orderId;
    body.paymentRequest = {
        urlSuccess: urlSuccess,
        urlFailure: urlFailure,
        urlCancel: urlCancel,
    };
    body.finalizeOrigin = 'VUE';
    if (authorizationToken) body.paymentRequest.authorizationToken = authorizationToken;
    if (captcha) body.captcha = captcha;
    
    return await $fetch('/api/neo/frontend/_api/checkout/finalizePendingOrder', {
            headers: {
                'bt-use-user-auth': 'true',
                'csrf-token': csrfToken,
            },
            method: 'POST',
            body: body,
        });
    // useLogger('checkout').info(resp)
    // return {
        // ...resp,
        // error: resp?.error
    // };
}

export async function isAddressValid(csrfToken: string, address: object, orderId: string, isShippingTarget: boolean) {
    const resp = await $fetch('/api/neo/frontend/_api/checkout/isAddressValid', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            address: address,
            orderId: orderId,
            isShippingTarget: isShippingTarget,
        },
    })
    return { data: resp, error: resp?.error} ;
}

export async function startKlarnaExpressOrderProcess(csrfToken: string, address: object, ppId: string) {
    const requestData: Record<string, unknown> = {};
    if (ppId) requestData.ppId = ppId;
    if (address) requestData.address = address;
    const abConfigContext = useABConfigCookie().value?.context as ABConfigContext | undefined;
    const paybackCardNumber = abConfigContext?.payback?.cardNumber;
    const paybackSCRID = getScrid();
    if (paybackCardNumber) requestData.paybackCardNumber = paybackCardNumber;
    if (paybackSCRID.value) requestData.paybackSCRID = paybackSCRID.value;

    const data = await $fetch('/api/neo/frontend/_api/checkout/startKlarnaExpressOrderProcess', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: requestData,
    });
    return ref(data);
}

export async function getPaypalPaymentUrls(
    csrfToken: string,
    succesUrl: string,
    cancelUrl: string,
    failureUrl: string,
    orderId: string,
) {
    let body = {
        urls: {
            succesUrl: succesUrl,
            cancelUrl: cancelUrl,
            failureUrl: failureUrl,
        },
    };
    body.orderId = orderId;
    const data = await $fetch('/api/neo/paypal-checkout-service/getPaymentUrls', {
        method: 'POST',
        body: body,
    });
    return ref(data);
}

export async function executePaypalPayment(csrfToken: string, paymentId: string) {
    const data = await $fetch('/api/neo/frontend/_api/paypal-checkout/executePayment', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            paymentId: paymentId,
        },
    });
    return ref(data);
}

export async function createPaypalExpressPayment(csrfToken: string) {
    const requestData: Record<string, unknown> = {};
    const abConfigContext = useABConfigCookie().value?.context as ABConfigContext | undefined;
    const paybackCardNumber = abConfigContext?.payback?.cardNumber;
    const paybackSCRID = getScrid();
    if (paybackCardNumber) requestData.paybackCardNumber = paybackCardNumber;
    if (paybackSCRID.value) requestData.paybackSCRID = paybackSCRID.value;

    const data = await $fetch('/api/neo/frontend/_api/paypal-checkout/createExpressPayment', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: requestData,
    });
    return ref(data);
}

export async function continuePaypalPayment(csrfToken: string, paymentId: string) {
    const data = await $fetch('/api/neo/frontend/_api/paypal-checkout/continuePayment', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            paymentId: paymentId,
        },
    });
    return ref(data);
}

async function getOrderStatus(csrfToken: string, orderId: string) {
    return await $fetch('/api/neo/frontend/_api/checkout/getOrderStatus', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrfToken,
        },
        method: 'POST',
        body: {
            orderId: orderId,
        },
    });
}

export async function wait(time) {
    await new Promise((resolve) =>
        setTimeout(() => {
            resolve();
        }, time),
    );
}

export async function orderApproved(csrfToken: string, orderId: string) {
    let data;
    try {
        data = await getOrderStatus(csrfToken, orderId);
    } catch (error) {
        //
    }
    if (data != 'APPROVED') {
        await wait(1000);
        await orderApproved(csrfToken, orderId);
    }
}

export function getBinsTotal(bins) {
    let binSum = 0;
    bins?.forEach((bin) => {
        binSum += getItemsSum(bin?.itemList?.items, false, false) + bin.price?.gross;
    });
    return binSum.toFixed(2);
}

export function getCartTotal(cart: ShoppingCart, unpromoted: boolean) {
    return getItemsSum(cart?.itemList, false, unpromoted);
}

export function getCartMwst(cart: ShoppingCart, unpromoted: boolean) {
    return getItemsSum(cart?.itemList, false, unpromoted) - getItemsSum(cart?.itemList, true, unpromoted);
}

const getItemsSum = (items: Array<ShopCartItem>, isGetNet: boolean, unpromoted: boolean) => {
    let itemsSum = 0;
    items?.forEach((item) => {
        let additionalItemsSum = 0;
        item?.additionalItems?.items?.forEach((additionalItem) => {
            additionalItemsSum += calcItemPrice(additionalItem, isGetNet, item.quantity, unpromoted);
        });
        itemsSum += calcItemPrice(item, isGetNet, item.quantity, unpromoted);
        itemsSum += additionalItemsSum;
    });
    return itemsSum;
};

const calcItemPrice = (item: NeoCartItem, isGetNet: boolean, quantity: number, unpromoted: boolean) => {
    let promSum = 0;
    if (!unpromoted) {
        for (var key in item?.promotions) {
            promSum += isGetNet ? item?.promotions[key].net : item?.promotions[key].gross;
        }
    }
    return (isGetNet ? item?.price?.net : item?.price?.gross) * quantity + promSum * quantity;
};

export function getRaw(rawData) {
    return isProxy(rawData) ? toRaw(rawData) : rawData;
}

export const updateCartRelevantStatesByCartType = (cart: ShoppingCart | null) => {
    useLogger('shoppingCartApi').info('Update cart states', cart);
    if (cart) {
        switch (cart.cartType) {
            case CartType.RESERVATION_CART:
                useCurrentReservationCart().value = cart;
                useReservationCartItemsCount().value = cart.overAllQuantity || 0;
                break;
            case CartType.SHOPPING_CART:
                useCurrentShoppingCart().value = cart;
                useShoppingCartItemsCount().value = cart.overAllQuantity || 0;
                break;
        }
    }
};

export enum ShoppingCartStep {
    CART = 'Warenkorb',
    ADDRESS = 'Adresse',
    CARRIER = 'Lieferart',
    PAYMENT = 'Zahlungsart',
    OVERVIEW = 'Übersicht',
}

export interface CheckoutNavigationStep {
    [index: number]: ShoppingCartStep;
}

export const shoppingCartSteps: CheckoutNavigationStep = {
    1: ShoppingCartStep.CART,
    2: ShoppingCartStep.ADDRESS,
    3: ShoppingCartStep.CARRIER,
    4: ShoppingCartStep.PAYMENT,
    5: ShoppingCartStep.OVERVIEW,
};

export const reservationCartSteps: CheckoutNavigationStep = {
    1: ShoppingCartStep.CART,
    2: ShoppingCartStep.ADDRESS,
    3: ShoppingCartStep.OVERVIEW,
};

export interface OrderItemPrice extends NeoPrice {
    basicPrice?: {
        basicArticleUnit: string;
        basicPriceQuantity: number;
    };
    customData: {
        expPrice: {
            onlineStore: number;
        };
    };
}

export enum AvailabilityStatus {
    AVAILABLE,
    NEXT_DAYS_AVAILABLE,
    PREORDER,
    NOT_IN_STORE,
    NOT_AVAILABLE,
    ONLY_IN_STORE,
    PROCURABLE,
    SOLD_OUT,
    NONE,
}

export enum OrderStatus {
    PENDING,
    CREATED,
    APPROVED,
    CANCELED,
    FULFILLED,
    DISCARDED,
}

export interface OrderItem {
    id: string;
    articleId: string;
    quantity: number;
    price: OrderItemPrice;
    itemOnDisplay: ExhibitionInfo;
    selectedShippingOptions: {
        options: object;
    };
    additionalItems: ItemList;
    selectedCarrier: string;
    selectedShippingMethod: string;
    availability: {
        color: AvailabilityStatus;
        minLeadTimeDays: number;
        maxLeadTimeDays: number;
    };
    promotions: {
        [key: string]: Price;
    };
    promotionValidForCount: {
        [key: string]: number;
    };
}

export interface Bin {
    itemList: ItemList;
    price: NeoSummedPrice;
    phoneRequired: boolean;
    shippingMethodOption: string;
    shippingCarrier: string;
    possibleCarrierOptions: {
        [key: string]: string[];
    };
}

export interface Order {
    customerId: string;
    invoiceAddress: Address;
    shippingAddress: Address;
    shippingBins: {
        bins: Bin[];
    };
    shippingOrders: string[];
    payments: string[];
    orderStatus: OrderStatus;
    client: string;
    created: number;
    lastStatusUpdate: number;
    currency: string;
    context: string;
    trackingClassesDone: string[];
    coupons: string[];
    additionalInfo: {
        [key: string]: boolean;
    };
    referencedOrder?: string;
    optimizedOrder?: string;
    customData: {
        expOrder: {
            effiOrderId: string;
        };
    };
    id: string;
    finalizeOrigin?: string;
    revision: number;
}

export async function getOrdersForMonth(date: number): Promise<Order[]> {
    const csrf = useNeoSession().value?.csrfToken;
    const cookieHeader = useRequestHeader('cookie');
    if (!csrf) return [];

    const data = await $fetch<Order[]>('/api/neo/frontend/_api/user/getOrdersForMonth', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrf,
            'cookie': cookieHeader,
        },
        method: 'POST',
        body: {
            date: date,
        },
    }).catch((error) => {
        useLogger('shoppingCartApi/getOrdersForMonth').error(error);
        return [];
    });
    return data;
}

export async function getPayedPayment(payments: string[]) {
    const csrf = useCurrentUserState().value?.csrfToken;
    if (!csrf) return [];

    const data = await $fetch('/api/neo/payment-service/getPayedPayment', {
        headers: {
            'bt-use-user-auth': 'true',
            'csrf-token': csrf,
        },
        method: 'POST',
        body: {
            payments: payments,
        },
    });
    return data;
}

export const getPathRepresentationFromCartType = (type: CartType): string => {
    let cartRepresentation = '';
    switch (type) {
        case CartType.SHOPPING_CART:
            cartRepresentation = 'shoppingcart';
            break;
        case CartType.RESERVATION_CART:
            cartRepresentation = 'reservationcart';
            break;
    }
    return cartRepresentation;
}